// Works out which resource each of the 5 ghosts can still be demanding.
//
// Colour semantics (verified by replaying every state in public/json):
//   G — this ghost wanted the resource it was given; it is satisfied from now on.
//   Y — some *still-unsolved* ghost other than this one wants that resource.
//   R — no still-unsolved ghost wants that resource.
// A ghost that has already been satisfied stops counting, which is why a resource
// can come back red in a later round even though a green ghost wanted it. A ghost
// that turns green in the current round already stops counting for that round.
//
// Two ghosts are allowed to want the same resource (it happens in ~89% of states,
// not just in 3-4 resource games), so there is no 1:1 ghost/resource mapping to
// lean on. Rather than approximate that with elimination rules, we enumerate every
// possible demand assignment and keep the ones that reproduce the colours the
// player actually saw. Ghosts already solved are pinned, so the search is at most
// numResources^5 (32768 at 8 resources, ~8ms) on the first round and far smaller
// after that.

const SLOTS = 5;

// Nothing has been played yet, so every ghost could want anything.
export const initialGhostDemands = (numResources) => ({
    possibilities: Array.from({ length: SLOTS }, () =>
        new Set(Array.from({ length: numResources }, (_, i) => i))
    ),
    jokerRecommended: false,
    jokerGhostIndex: null,
    assignmentCount: Math.pow(numResources, SLOTS),
    certain: false,
    contradiction: false,
});

const getCompletedRounds = (picks, results) => {
    const completed = [];
    for (let round = 1; round <= 5; round++) {
        if (picks[round]?.length > 0 && results[round]?.some(r => r && r !== '')) {
            completed.push(round);
        }
    }
    return completed;
};

// Ghosts that have been satisfied, and the resource each of them turned out to want.
const getSolvedGhosts = (picks, results, completedRounds) => {
    const known = new Array(SLOTS).fill(null);
    for (const round of completedRounds) {
        for (let slot = 0; slot < SLOTS; slot++) {
            const pick = picks[round][slot];
            if (results[round][slot] === 'G' && pick !== '-' && pick != null) {
                known[slot] = pick;
            }
        }
    }
    return known;
};

// Would this assignment of demands have produced the colours the player recorded?
const isConsistent = (demand, picks, results, completedRounds) => {
    const solved = new Array(SLOTS).fill(false);

    for (const round of completedRounds) {
        const roundPicks = picks[round];
        const roundResults = results[round];

        const greenThisRound = new Array(SLOTS).fill(false);
        for (let slot = 0; slot < SLOTS; slot++) {
            const pick = roundPicks[slot];
            if (pick !== '-' && pick != null && demand[slot] === pick) greenThisRound[slot] = true;
        }

        for (let slot = 0; slot < SLOTS; slot++) {
            const pick = roundPicks[slot];
            const observed = roundResults[slot];
            // '-' is a ghost that was already satisfied, '' is a slot the player
            // has not coloured yet — neither tells us anything.
            if (pick === '-' || pick == null || !observed || solved[slot]) continue;

            let colour;
            if (demand[slot] === pick) {
                colour = 'G';
            } else {
                let wantedByOther = false;
                for (let ghost = 0; ghost < SLOTS; ghost++) {
                    if (ghost === slot || solved[ghost] || greenThisRound[ghost]) continue;
                    if (demand[ghost] === pick) { wantedByOther = true; break; }
                }
                colour = wantedByOther ? 'Y' : 'R';
            }
            if (colour !== observed) return false;
        }

        for (let slot = 0; slot < SLOTS; slot++) {
            if (greenThisRound[slot] || roundPicks[slot] === '-') solved[slot] = true;
        }
    }

    return true;
};

export const calculateGhostDemands = (resources, picks, results) => {
    const numResources = resources.length;
    const completedRounds = getCompletedRounds(picks, results);
    const known = getSolvedGhosts(picks, results, completedRounds);

    const possibilities = Array.from({ length: SLOTS }, () => new Set());
    const unknownGhosts = [];
    for (let ghost = 0; ghost < SLOTS; ghost++) {
        if (known[ghost] === null) unknownGhosts.push(ghost);
    }

    // Enumerate every demand the unsolved ghosts could have; solved ones are pinned.
    const demand = new Array(SLOTS).fill(0);
    for (let ghost = 0; ghost < SLOTS; ghost++) {
        if (known[ghost] !== null) demand[ghost] = known[ghost];
    }

    let assignmentCount = 0;
    const total = Math.pow(numResources, unknownGhosts.length);
    for (let code = 0; code < total; code++) {
        let remainder = code;
        for (const ghost of unknownGhosts) {
            demand[ghost] = remainder % numResources;
            remainder = (remainder / numResources) | 0;
        }
        if (!isConsistent(demand, picks, results, completedRounds)) continue;
        assignmentCount++;
        for (const ghost of unknownGhosts) possibilities[ghost].add(demand[ghost]);
    }

    // Nothing fits — the colours as entered cannot happen (usually a mis-click).
    // Say we know nothing rather than showing a confidently wrong deduction.
    const contradiction = assignmentCount === 0;
    if (contradiction) {
        for (const ghost of unknownGhosts) {
            possibilities[ghost] = new Set(Array.from({ length: numResources }, (_, i) => i));
        }
    }

    // A joker is worth spending when exactly one unsolved ghost is still ambiguous.
    let jokerRecommended = false;
    let jokerGhostIndex = null;
    if (completedRounds.length >= 2 && !contradiction) {
        const ambiguous = unknownGhosts.filter(ghost => possibilities[ghost].size >= 2);
        if (ambiguous.length === 1) {
            jokerRecommended = true;
            jokerGhostIndex = ambiguous[0];
        }
    }

    console.log('[ghostDemands] possibilities:', possibilities.map((s, i) => `ghost ${i}: [${[...s].map(idx => resources[idx]).join(', ')}] (${s.size})`));
    console.log('[ghostDemands] assignments:', assignmentCount, 'jokerRecommended:', jokerRecommended, 'jokerGhostIndex:', jokerGhostIndex);

    // assignmentCount === 1 means the whole board is pinned down — the brain reports
    // a 100% win chance in exactly those states.
    return { possibilities, jokerRecommended, jokerGhostIndex, assignmentCount, certain: assignmentCount === 1, contradiction };
};
